import React, { useCallback } from 'react'

import { useDispatch, useSelector } from 'react-redux'

import type { MenuRecipeTileRequestParams } from '@library/menu-service'

import { useCollectionRecipeSets, useCurrentCollectionId } from 'hooks/menu-collections'
import type { RecipeUUID } from 'reducers/menu-recipes/recipes'
import type { RefreshRecipeTile } from 'routes/Menu/MenuRecipesPage/RecipeList/RecipeList'
import { useRecipeDetailData } from 'routes/Menu/components/Detail/hooks/useRecipeDetailData'
import { useRecipeDetailSelectedPortion } from 'routes/Menu/components/Detail/hooks/useRecipeDetailSelectedPortion'
import { getBasketDate, getBasketMenuUuid, getBasketRecipeNumPortionsForId } from 'selectors/basket'

import { closeRecipeDetails } from '../../../actions/closeRecipeDetails'
import {
  getMenuRecipeForDetails,
  getMenuRecipeUuidForDetails,
} from '../../../selectors/menuRecipeDetails'
import { Detail } from '../Detail'

interface DetailOverlayProps {
  showOverlay?: boolean
  refreshRecipeTile?: RefreshRecipeTile
}

/**
 * Returns the position of the recipe in the collection recipe sets list,
 * works for cases when `recipeId` is an option in the recipe set
 *
 * If recipe is not in the collection, returns undefined
 */
const useRecipePosition = (recipeId: RecipeUUID | null): number | undefined => {
  const currentCollectionId = useCurrentCollectionId()
  const currentCollectionRecipeSets = useCollectionRecipeSets(currentCollectionId)

  if (!recipeId) {
    return undefined
  }

  const recipeIndex = currentCollectionRecipeSets.findIndex(({ recipeIds }) =>
    recipeIds.has(recipeId),
  )

  return recipeIndex >= 0 ? recipeIndex + 1 : undefined
}

const DetailOverlay = ({ showOverlay: showOverlayProp, refreshRecipeTile }: DetailOverlayProps) => {
  const showOverlay = Boolean(useSelector(getMenuRecipeUuidForDetails)) && showOverlayProp
  const recipeUuid = useSelector(getMenuRecipeUuidForDetails)
  const menuRecipeDetailShow = recipeUuid || ''
  const recipePosition = useRecipePosition(recipeUuid as RecipeUUID | null)

  const recipeNumPortions = useSelector(
    getBasketRecipeNumPortionsForId(recipeUuid as RecipeUUID | null),
  )
  const detailRecipe = useSelector(getMenuRecipeForDetails)
  const dispatch = useDispatch()
  const onClose = useCallback(() => dispatch(closeRecipeDetails()), [dispatch])
  const deliveryDate = useSelector(getBasketDate)
  const menuId = useSelector(getBasketMenuUuid)
  const collectionId = useCurrentCollectionId()
  const shouldFetchOptions = true
  const { selectedNumPortions, setSelectedNumPortions } = useRecipeDetailSelectedPortion({
    numPortions: recipeNumPortions,
    recipeUuid,
  })
  const handleUpdatePortions = useCallback(
    (updatedNumPortions: number) => {
      setSelectedNumPortions(updatedNumPortions)

      if (!recipeUuid) {
        return
      }

      void refreshRecipeTile?.(
        recipeUuid,
        updatedNumPortions as MenuRecipeTileRequestParams['numPortions'],
      )
    },
    [recipeUuid, refreshRecipeTile, setSelectedNumPortions],
  )

  const { loading, recipe, error, reload } = useRecipeDetailData({
    menuId,
    recipeUuid,
    numPortions: selectedNumPortions,
    deliveryDate,
    shouldFetchOptions,
    collectionId,
  })

  if (!(showOverlay && detailRecipe && menuRecipeDetailShow)) {
    return null
  }

  return (
    <Detail
      uuid={detailRecipe.id}
      title={detailRecipe.name}
      showOverlay={showOverlay}
      loading={loading}
      error={error}
      recipe={recipe}
      reload={reload}
      position={recipePosition?.toString()}
      onClose={onClose}
      numPortions={selectedNumPortions}
      onUpdatePortions={handleUpdatePortions}
    />
  )
}

export { DetailOverlay }
